import * as React from 'react';
import { ItemSelector, Text } from 'vet-bones/components';
import { emptyArray } from 'vet-bones/constants';
import { isEqual } from 'vet-bones/utils';
import { SelectItem } from 'vet-bones/utils/select';

import { createNewSelectItemRenderer } from 'src/components/select/createNewSelectItemRenderer';
import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_ATTRIBUTE_TYPE,
  PROJECT_DIAGRAM_ENTITY_DRAWER_DATATYPES_TYPE,
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import { AddButton } from 'src/containers/ProjectDiagram/components/ProjectDiagramEntityDrawerButtons';
import { useCreateModelConceptAttributeFromQuery } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useCreateModelConceptAttributeFromQuery';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { projectAttributeItemsSelector } from 'src/selectors/projectAttributeItems';
import { projectAttributeRangesByIriSelector } from 'src/selectors/projectAttributeRangesByIri';
import { projectDatatypeItemsSelector } from 'src/selectors/projectDatatypeItems';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectDiagramAttributesTableAddRowProps = {
  currentAttributeIris: Set<string>;
  onAddAttribute: (item: AttributeTableItem) => void;
  additionalIris?: Set<string>;
};

export const ProjectDiagramAttributesTableAddRow: React.VFC<
  ProjectDiagramAttributesTableAddRowProps
> = ({ onAddAttribute, currentAttributeIris, additionalIris }) => {
  const { projectId } = useRouterParams();
  const combinedIris = React.useMemo(() => {
    const combined = new Set<string>();
    currentAttributeIris.forEach((iri) => combined.add(iri));
    additionalIris?.forEach((iri) => combined.add(iri));
    return combined;
  }, [currentAttributeIris, additionalIris]);
  const createAttributeFromQuery =
    useCreateModelConceptAttributeFromQuery(combinedIris);

  const [attribute, setAttribute] = React.useState<AttributeTableItem | null>(
    null
  );

  const [selectedDatatypes, selectDatatypes] = React.useState<SelectItem[]>([]);

  const handleSelectAttribute = (selected: AttributeTableItem | null) => {
    if (!selected || (selected.isNewlyCreated && !selected.text)) {
      setAttribute(null);
    } else {
      setAttribute(selected);
    }
  };

  const handleSubmit = () => {
    if (!attribute) {
      return;
    }
    onAddAttribute({
      ...attribute,
      ranges: isExistingAttributeSelected ? existingRanges : selectedDatatypes,
    });
    setAttribute(null);
    selectDatatypes([]);
  };

  const allAttributeItems = useReduxSelector(
    projectAttributeItemsSelector(projectId),
    isEqual
  );

  const labelsByIri = useReduxSelector(
    projectLabelsByIriSelector(projectId),
    isEqual
  );

  const ranges = useReduxSelector(
    projectAttributeRangesByIriSelector(projectId),
    isEqual
  );

  const availableAttributeItems = React.useMemo(
    () =>
      allAttributeItems.filter((item) => !currentAttributeIris.has(item.value)),
    [allAttributeItems, currentAttributeIris]
  );

  const datatypeItems = useReduxSelector(
    projectDatatypeItemsSelector(projectId)
  );

  const existingRanges: SelectItem[] = attribute
    ? ranges[attribute?.value] || emptyArray
    : (emptyArray as any);

  const isExistingAttributeSelected =
    attribute &&
    availableAttributeItems.some((item) => item.value === attribute.value);
  const isMissingAttribute = !attribute;
  const isMissingDatatypes =
    !isExistingAttributeSelected && !selectedDatatypes.length;

  let tooltipContent = '';
  if (isMissingAttribute) {
    tooltipContent =
      copy.components.projectDiagram.attributesTable.missingAttribute;
  } else if (isMissingDatatypes) {
    tooltipContent =
      copy.components.projectDiagram.attributesTable.missingDatatypes;
  }
  const disabled = Boolean(tooltipContent);

  return (
    <tr data-testid={TEST_IDS.projectDiagram.entityDrawer.tableAddRow}>
      <td>
        <ItemSelector.Single
          buttonText={attribute ? attribute.text : ''}
          creatable
          createNewItemFromQuery={createAttributeFromQuery}
          createNewItemRenderer={createNewSelectItemRenderer}
          createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
            ''
          )}
          filterOrCreateText={copy.components.defaults.filterOrCreate}
          filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
          items={availableAttributeItems}
          itemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
          onSelect={handleSelectAttribute}
          resetOnClose
          selected={attribute}
          testId={getProjectDiagramEntityDrawerSelectorTestId(
            PROJECT_DIAGRAM_ENTITY_DRAWER_ATTRIBUTE_TYPE
          )}
        />
      </td>
      <td>
        {attribute && !existingRanges.length ? (
          <ItemSelector.Multi
            buttonText={
              selectedDatatypes.length
                ? selectedDatatypes.map((datatype) => datatype.text).join(', ')
                : ''
            }
            filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
            items={datatypeItems}
            itemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
            onSelect={selectDatatypes}
            resetOnClose
            selected={selectedDatatypes}
            testId={getProjectDiagramEntityDrawerSelectorTestId(
              PROJECT_DIAGRAM_ENTITY_DRAWER_DATATYPES_TYPE
            )}
          />
        ) : (
          <Text className="sd-drawer-project-diagram-entity-table--standin-selector">
            {existingRanges
              .map((range) => getLabelForIri(labelsByIri, range.value))
              .join(', ')}
          </Text>
        )}
      </td>
      <td />
      <td>
        <AddButton
          disabled={disabled}
          onClick={handleSubmit}
          tooltip={tooltipContent}
        />
      </td>
    </tr>
  );
};
